import React, { useState, useRef, useEffect } from 'react';
import { HelpCircle, ExternalLink, X } from 'lucide-react';
import { getHelpPreview } from '@/pages/HelpPage';

/**
 * Top-bar help affordance. Click the HelpCircle to open a compact popover
 * with a 1-paragraph preview of the current page's help content; from there
 * the user can either escape to the full docs in a new tab or dismiss and
 * stay in flow.
 *
 * Props:
 *   section: string — help section id (e.g. "tenant-lifecycle")
 */
export default function HelpPopover({ section }) {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  const info = getHelpPreview(section);
  const Icon = info?.icon || HelpCircle;
  const helpUrl = `/help?s=${encodeURIComponent(section)}`;

  // Close on outside click or Escape.
  useEffect(() => {
    if (!open) return undefined;
    const onClick = (e) => {
      if (popRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        title="Help for this page"
        aria-label="Help for this page"
        aria-haspopup="dialog"
        aria-expanded={open}
        data-testid="page-help-link"
        data-help-section={section}
        className={`inline-flex items-center gap-1.5 px-2.5 h-9 rounded-xl transition-colors text-xs ${open ? 'bg-muted/40 text-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/40'}`}
      >
        <HelpCircle className="w-4 h-4" />
        <span className="hidden md:inline">Help</span>
      </button>

      {open && (
        <div
          ref={popRef}
          role="dialog"
          aria-label="Page help"
          data-testid="page-help-popover"
          className="absolute right-0 top-11 w-[360px] glass-card rounded-xl shadow-2xl border border-border/50 z-[120] overflow-hidden"
        >
          <div className="flex items-start gap-3 p-4 pb-3">
            <div className="rounded-lg bg-primary/10 border border-primary/20 p-2 flex-shrink-0">
              <Icon className="w-4 h-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-heading font-semibold text-foreground truncate" data-testid="help-popover-title">
                  {info?.title || 'Help'}
                </h3>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  className="rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 p-0.5 flex-shrink-0"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
              {info?.preview ? (
                <p className="text-xs text-foreground/80 leading-relaxed mt-1.5" data-testid="help-popover-preview">
                  {info.preview}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground mt-1.5 italic">Full reference available in the docs.</p>
              )}
            </div>
          </div>
          <div className="flex items-center justify-between px-4 py-2.5 border-t border-border/40 bg-muted/20">
            <span className="text-[10px] text-muted-foreground">Need more detail?</span>
            <a
              href={helpUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid="help-popover-open-full"
              className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
            >
              Open full docs <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
